import camelcaseKeys from 'camelcase-keys';
import FuzzySearch from 'fuzzy-search';

import { customFuzzyFind, getEnhancedPolicyData } from '../../../components/CertificateCreationFlow/util';
import DataTypes from '../../../constants/dataTypes';
import { PolicyTypes } from '../../../constants/policyTypes';
import { isValidDateString } from '../../../utils/dateUtil';

export const setGlPolicyData = (
  gLPolicies,
  insuranceCompaniesByLetter,
  glType,
  glRequired,
  dispatch,
  setPolicyData,
  policyRequirements
) => {
  if (gLPolicies?.length) {
    const firstGlPolicy = gLPolicies[0];
    const {
      base_details: baseDetails,
      policy_details: policyDetails
    } = firstGlPolicy || {};

    const {
      insurer_letter: insurerLetter,
      policy_number: policyNumber,
      effective_date: effectiveDate,
      expiration_date: expirationDate,
      additional_insured: additionalInsured,
      waiver_of_subrogation: waiverOfSubrogation
    } = getBaseDetails(baseDetails);

    const {
      claims_made_checkbox: claimsMade,
      occurrence_checkbox: occurrence,
      commercial_general_liability_checkbox: commGenLiability,
      agg_limit_applies_per_policy_checkbox: perPolicy,
      agg_limit_applies_per_project_checkbox: perProject,
      agg_limit_applies_per_location_checkbox: perLocation,
      agg_limit_applies_per_other_checkbox: perOther,
      limits
    } = policyDetails || {};

    const {
      each_occurrence: eachOccurrenceLimit,
      damage_to_rented_premises: damageLimit,
      medical_expense: medicalExpenseLimit,
      personal_and_advertising_injury: injuryLimit,
      general_aggregate: generalAggregateLimit,
      products_completed_operations_aggregate: productsLimit
    } = limits || {};

    const glPropertyMap = {
      general_liability_comm: 'comm',
      general_liability_claims_made: 'claims_made',
      general_liability_occur: 'occur',
      general_liability_gen_agg_policy: 'gen_agg_policy',
      general_liability_gen_agg_project: 'gen_agg_project',
      general_liability_gen_agg_loc: 'gen_agg_loc',
      other: 'gen_agg_other',
      general_liability_limits_each_occur: 'limits_each_occur',
      general_liability_limits_damage: 'limits_damage',
      general_liability_limits_med_exp: 'limits_med_exp',
      general_liability_limits_injury: 'limits_injury',
      general_liability_limits_gen_agg: 'limits_gen_agg',
      general_liability_limits_products: 'limits_products'
    };

    const insuranceCompany = insuranceCompaniesByLetter[insurerLetter] ?? null;
    const glProperties = glType?.properties;

    if (glRequired) {
      const glDefinedProperties = {
        [`properties_${glProperties.find(prop => prop.name === glPropertyMap.general_liability_comm).id}`]: getCheckboxValueFromObject(commGenLiability),
        [`properties_${glProperties.find(prop => prop.name === glPropertyMap.general_liability_claims_made).id}`]: getCheckboxValueFromObject(claimsMade),
        [`properties_${glProperties.find(prop => prop.name === glPropertyMap.general_liability_occur).id}`]: getCheckboxValueFromObject(occurrence),
        [`properties_${glProperties.find(prop => prop.name === glPropertyMap.general_liability_gen_agg_policy).id}`]: getCheckboxValueFromObject(perPolicy),
        [`properties_${glProperties.find(prop => prop.name === glPropertyMap.general_liability_gen_agg_project).id}`]: getCheckboxValueFromObject(perProject),
        [`properties_${glProperties.find(prop => prop.name === glPropertyMap.general_liability_gen_agg_loc).id}`]: getCheckboxValueFromObject(perLocation),
        [`properties_${glProperties.find(prop => prop.name === glPropertyMap.other).id}`]: getCheckboxValueFromObject(perOther),
        [`properties_${glProperties.find(prop => prop.name === glPropertyMap.general_liability_limits_each_occur).id}`]: getLimitValueFromObject(eachOccurrenceLimit),
        [`properties_${glProperties.find(prop => prop.name === glPropertyMap.general_liability_limits_damage).id}`]: getLimitValueFromObject(damageLimit),
        [`properties_${glProperties.find(prop => prop.name === glPropertyMap.general_liability_limits_med_exp).id}`]: getLimitValueFromObject(medicalExpenseLimit),
        [`properties_${glProperties.find(prop => prop.name === glPropertyMap.general_liability_limits_injury).id}`]: getLimitValueFromObject(injuryLimit),
        [`properties_${glProperties.find(prop => prop.name === glPropertyMap.general_liability_limits_gen_agg).id}`]: getLimitValueFromObject(generalAggregateLimit),
        [`properties_${glProperties.find(prop => prop.name === glPropertyMap.general_liability_limits_products).id}`]: getLimitValueFromObject(productsLimit)
      };

      const glPolicyDetails = {
        details: {
          additional_insured: !!additionalInsured,
          effective_at: isValidDateString(effectiveDate)
            ? new Date(effectiveDate)
            : null,
          expires_at: isValidDateString(expirationDate)
            ? new Date(expirationDate)
            : null,
          number: policyNumber,
          subrogation_waived: !!waiverOfSubrogation
        },
        insurance_company: insuranceCompany,
        ...glDefinedProperties
      };

      const glEnhancedPolicyData = getEnhancedPolicyData(
        glType,
        camelcaseKeys(glPolicyDetails?.insurance_company, { deep: true }),
        glPolicyDetails
      );

      dispatch(setPolicyData(glType.id, glEnhancedPolicyData));
    } else {
      // do a fuzzy check to see if there's a custom general policy type
      const policyTypeSearcher = new FuzzySearch(policyRequirements, ['displayName', 'name'], { sort: true });
      const fuzzyMatchGlTerms = ['general liability', 'general'];

      // eslint-disable-next-line no-plusplus
      for (let i = 0; i < fuzzyMatchGlTerms.length; i++) {
        const fuzzyMatchName = fuzzyMatchGlTerms[i];
        const fuzzyMatch = policyTypeSearcher.search(fuzzyMatchName);

        if (fuzzyMatch.length) {
          const customGlRequirement = fuzzyMatch[0];

          const propertySearcher = new FuzzySearch(glProperties, ['displayName', 'name'], { sort: true });
          const fuzzyGlProperties = {};

          const commProperty = fuzzyMatchSearch(propertySearcher, ['Commercial General Liability', 'Commercial General'], DataTypes.Boolean);
          const claimsProperty = fuzzyMatchSearch(propertySearcher, ['Per Claims Made', 'Per Claims', 'Claims-Made', 'Claims'], DataTypes.Boolean);
          const occurProperty = fuzzyMatchSearch(propertySearcher, ['Per Occurrence', 'Per Ocurrence', 'Per Occurence'], DataTypes.Boolean);
          const genAggPolicyProperty = fuzzyMatchSearch(propertySearcher, ['General Aggregate Limit Applies Per Policy', 'Per Policy'], DataTypes.Boolean);
          const genAggProjectProperty = fuzzyMatchSearch(propertySearcher, ['General Aggregate Limit Applies Per Project', 'Per Project'], DataTypes.Boolean);
          const genAggLocProperty = fuzzyMatchSearch(propertySearcher, ['General Aggregate Limit Applies Per Location', 'Per Location', 'Per Loc'], DataTypes.Boolean);
          const otherProperty = fuzzyMatchSearch(propertySearcher, ['General Aggregate Limit Applies Per Other', 'Per Other', 'Other'], DataTypes.Boolean);
          const eachOccurProperty = fuzzyMatchSearch(propertySearcher, ['Each Occurrence', 'Each Ocurrence', 'Each Occurence', 'Ea Occur', 'Each Occur', DataTypes.Monetary]);
          const damageProperty = fuzzyMatchSearch(propertySearcher, ['Damage to Rented Premises', 'Rented Premises', 'Damage to', 'Damage', DataTypes.Monetary]);
          const medExpProperty = fuzzyMatchSearch(propertySearcher, ['Medical Expenses', 'Medical Exp', 'Med Exp', DataTypes.Monetary]);
          const injuryProperty = fuzzyMatchSearch(propertySearcher, ['Personal and Adv Injury', 'Personal Injury', 'Personal', 'Personal &', 'Injury', DataTypes.Monetary]);
          const genAggLimitProperty = fuzzyMatchSearch(propertySearcher, ['General Aggregate', 'General Agregate', 'Gen Agg', DataTypes.Monetary]);
          const productProperty = fuzzyMatchSearch(propertySearcher, ['Products - Comp/OP Agg', 'Products -', 'Products', DataTypes.Monetary]);

          if (commProperty) {
            fuzzyGlProperties[`properties_${commProperty.id}`] = getCheckboxValueFromObject(commGenLiability);
          }

          if (claimsProperty) {
            fuzzyGlProperties[`properties_${claimsProperty.id}`] = getCheckboxValueFromObject(claimsMade);
          }

          if (occurProperty) {
            fuzzyGlProperties[`properties_${occurProperty.id}`] = getCheckboxValueFromObject(occurrence);
          }

          if (genAggPolicyProperty) {
            fuzzyGlProperties[`properties_${genAggPolicyProperty.id}`] = getCheckboxValueFromObject(perPolicy);
          }

          if (genAggProjectProperty) {
            fuzzyGlProperties[`properties_${genAggProjectProperty.id}`] = getCheckboxValueFromObject(perProject);
          }

          if (genAggLocProperty) {
            fuzzyGlProperties[`properties_${genAggLocProperty.id}`] = getCheckboxValueFromObject(perLocation);
          }

          if (otherProperty) {
            fuzzyGlProperties[`properties_${otherProperty.id}`] = getCheckboxValueFromObject(perOther);
          }

          if (eachOccurProperty) {
            fuzzyGlProperties[`properties_${eachOccurProperty.id}`] = getLimitValueFromObject(eachOccurrenceLimit);
          }

          if (damageProperty) {
            fuzzyGlProperties[`properties_${damageProperty.id}`] = getLimitValueFromObject(damageLimit);
          }

          if (medExpProperty) {
            fuzzyGlProperties[`properties_${medExpProperty.id}`] = getLimitValueFromObject(medicalExpenseLimit);
          }

          if (injuryProperty) {
            fuzzyGlProperties[`properties_${injuryProperty.id}`] = getLimitValueFromObject(injuryLimit);
          }

          if (genAggLimitProperty) {
            fuzzyGlProperties[`properties_${genAggLimitProperty.id}`] = getLimitValueFromObject(generalAggregateLimit);
          }

          if (productProperty) {
            fuzzyGlProperties[`properties_${productProperty.id}`] = getLimitValueFromObject(productsLimit);
          }

          const fuzzyGlPolicyDetails = {
            details: {
              additional_insured: !!additionalInsured,
              effective_at: isValidDateString(effectiveDate)
                ? new Date(effectiveDate)
                : null,
              expires_at: isValidDateString(expirationDate)
                ? new Date(expirationDate)
                : null,
              number: policyNumber,
              subrogation_waived: !!waiverOfSubrogation
            },
            insurance_company: insuranceCompany,
            ...fuzzyGlProperties
          };

          const fuzzyGlEnhancedCorePolicyData = getEnhancedPolicyData(
            customGlRequirement,
            camelcaseKeys(fuzzyGlPolicyDetails?.insurance_company, { deep: true }),
            fuzzyGlPolicyDetails
          );

          dispatch(setPolicyData(customGlRequirement.id, fuzzyGlEnhancedCorePolicyData));
        }
      }
    }
  }
};

export const setAutoPolicyData = (
  autoPolicies,
  insuranceCompaniesByLetter,
  autoType,
  autoRequired,
  dispatch,
  setPolicyData,
  policyRequirements
) => {
  if (autoPolicies?.length) {
    const firstAutoPolicy = autoPolicies[0];

    const {
      base_details: baseDetails,
      policy_details: policyDetails
    } = firstAutoPolicy || {};

    const {
      insurer_letter: insurerLetter,
      policy_number: policyNumber,
      effective_date: effectiveDate,
      expiration_date: expirationDate,
      additional_insured: additionalInsured,
      waiver_of_subrogation: waiverOfSubrogation
    } = getBaseDetails(baseDetails);

    const {
      any_auto_checkbox: anyAuto,
      owned_autos_checkbox: ownedAutos,
      scheduled_autos_checkbox: scheduledAutos,
      hired_autos_checkbox: hiredAutos,
      non_owned_autos_checkbox: nonOwnedAutos,
      limits
    } = policyDetails || {};

    const {
      combined_single_limit: combinedLimit,
      bodily_injury_per_person: perPersonLimit,
      bodily_injury_per_accident: perAccidentLimit,
      property_damage: propertyDamageLimit
    } = limits || {};

    const autoPropertyMap = {
      auto_liability_any_auto: 'any_auto',
      auto_liability_owned_autos: 'owned_autos',
      auto_liability_scheduled_autos: 'scheduled_autos',
      auto_liability_hired_autos: 'hired_autos',
      auto_liability_non_owned_autos: 'non_owned_autos',
      auto_liability_limits_combined_single: 'limits_combined_single',
      auto_liability_limits_injury_person: 'limits_injury_person',
      auto_liability_limits_injury_accident: 'limits_injury_accident',
      auto_liability_limits_property_damage: 'limits_property_damage'
    };

    const insuranceCompany = insuranceCompaniesByLetter[insurerLetter] ?? null;
    const autoProperties = autoType?.properties;

    if (autoRequired) {
      const autoDefinedProperties = {
        [`properties_${autoProperties.find(prop => prop.name === autoPropertyMap.auto_liability_any_auto).id}`]: getCheckboxValueFromObject(anyAuto),
        [`properties_${autoProperties.find(prop => prop.name === autoPropertyMap.auto_liability_owned_autos).id}`]: getCheckboxValueFromObject(ownedAutos),
        [`properties_${autoProperties.find(prop => prop.name === autoPropertyMap.auto_liability_scheduled_autos).id}`]: getCheckboxValueFromObject(scheduledAutos),
        [`properties_${autoProperties.find(prop => prop.name === autoPropertyMap.auto_liability_hired_autos).id}`]: getCheckboxValueFromObject(hiredAutos),
        [`properties_${autoProperties.find(prop => prop.name === autoPropertyMap.auto_liability_non_owned_autos).id}`]: getCheckboxValueFromObject(nonOwnedAutos),
        [`properties_${autoProperties.find(prop => prop.name === autoPropertyMap.auto_liability_limits_combined_single).id}`]: getLimitValueFromObject(combinedLimit),
        [`properties_${autoProperties.find(prop => prop.name === autoPropertyMap.auto_liability_limits_injury_person).id}`]: getLimitValueFromObject(perPersonLimit),
        [`properties_${autoProperties.find(prop => prop.name === autoPropertyMap.auto_liability_limits_injury_accident).id}`]: getLimitValueFromObject(perAccidentLimit),
        [`properties_${autoProperties.find(prop => prop.name === autoPropertyMap.auto_liability_limits_property_damage).id}`]: getLimitValueFromObject(propertyDamageLimit)
      };

      const autoPolicyDetails = {
        details: {
          additional_insured: !!additionalInsured,
          effective_at: isValidDateString(effectiveDate)
            ? new Date(effectiveDate)
            : null,
          expires_at: isValidDateString(expirationDate)
            ? new Date(expirationDate)
            : null,
          number: policyNumber,
          subrogation_waived: !!waiverOfSubrogation
        },
        insurance_company: insuranceCompany,
        ...autoDefinedProperties
      };

      const enhancedAutoPolicyData = getEnhancedPolicyData(
        autoType,
        camelcaseKeys(autoPolicyDetails?.insurance_company, { deep: true }),
        autoPolicyDetails
      );

      dispatch(setPolicyData(autoType.id, enhancedAutoPolicyData));
    } else {
      // do a fuzzy check to see if there's a custom auto policy type
      const policyTypeSearcher = new FuzzySearch(policyRequirements, ['displayName', 'name'], { sort: true });
      const fuzzyMatchAutoTerms = ['automobile liability', 'auto liability', 'auto'];

      // eslint-disable-next-line no-plusplus
      for (let i = 0; i < fuzzyMatchAutoTerms.length; i++) {
        const fuzzyMatchName = fuzzyMatchAutoTerms[i];
        const fuzzyMatch = policyTypeSearcher.search(fuzzyMatchName);

        if (fuzzyMatch.length) {
          const customAutoRequirement = fuzzyMatch[0];

          const propertySearcher = new FuzzySearch(autoProperties, ['displayName', 'name'], { sort: true });
          const fuzzyAutoProperties = {};

          const anyAutoProperty = fuzzyMatchSearch(propertySearcher, ['Any Auto'], DataTypes.Boolean);
          const ownedAutoProperty = fuzzyMatchSearch(propertySearcher, ['Owned Auto', 'Owned Auto'], DataTypes.Boolean);
          const scheduledAutoProperty = fuzzyMatchSearch(propertySearcher, ['Scheduled Autos', 'Scheduled Auto', 'Scheduled'], DataTypes.Boolean);
          const hiredAutoProperty = fuzzyMatchSearch(propertySearcher, ['Hired Autos Only', 'Hired Auto'], DataTypes.Boolean);
          const nonOwnedAutoProperty = fuzzyMatchSearch(propertySearcher, ['Non-Owned Autos Only', 'Non-Owned Auto', 'Non-Owned', 'Non Owned'], DataTypes.Boolean);
          const combinedSingleProperty = fuzzyMatchSearch(
            propertySearcher,
            ['Combined Single Limit Each Accident', 'Combined Single Limit Ea Accident', 'Combined Single Limit'],
            DataTypes.Monetary
          );
          const injuryLimitProperty = fuzzyMatchSearch(propertySearcher, ['Bodily Injury Per Person', 'Injury Person'], DataTypes.Monetary);
          const injuryAccidentLimitProperty = fuzzyMatchSearch(propertySearcher, ['Bodily Injury Per Accident', 'Injury Accident'], DataTypes.Monetary);
          const propertyLimitProperty = fuzzyMatchSearch(propertySearcher, ['Property Damage Per Accident', 'Property Damage'], DataTypes.Monetary);

          if (anyAutoProperty) {
            fuzzyAutoProperties[`properties_${anyAutoProperty.id}`] = getCheckboxValueFromObject(anyAuto);
          }

          if (ownedAutoProperty) {
            fuzzyAutoProperties[`properties_${ownedAutoProperty.id}`] = getCheckboxValueFromObject(ownedAutos);
          }

          if (scheduledAutoProperty) {
            fuzzyAutoProperties[`properties_${scheduledAutoProperty.id}`] = getCheckboxValueFromObject(scheduledAutos);
          }

          if (hiredAutoProperty) {
            fuzzyAutoProperties[`properties_${hiredAutoProperty.id}`] = getCheckboxValueFromObject(hiredAutos);
          }

          if (nonOwnedAutoProperty) {
            fuzzyAutoProperties[`properties_${nonOwnedAutoProperty.id}`] = getCheckboxValueFromObject(nonOwnedAutos);
          }

          if (combinedSingleProperty) {
            fuzzyAutoProperties[`properties_${combinedSingleProperty.id}`] = getLimitValueFromObject(combinedLimit);
          }

          if (injuryLimitProperty) {
            fuzzyAutoProperties[`properties_${injuryLimitProperty.id}`] = getLimitValueFromObject(perPersonLimit);
          }

          if (injuryAccidentLimitProperty) {
            fuzzyAutoProperties[`properties_${injuryAccidentLimitProperty.id}`] = getLimitValueFromObject(perAccidentLimit);
          }

          if (propertyLimitProperty) {
            fuzzyAutoProperties[`properties_${propertyLimitProperty.id}`] = getLimitValueFromObject(propertyDamageLimit);
          }

          const fuzzyAutoPolicyDetails = {
            details: {
              additional_insured: !!additionalInsured,
              effective_at: isValidDateString(effectiveDate)
                ? new Date(effectiveDate)
                : null,
              expires_at: isValidDateString(expirationDate)
                ? new Date(expirationDate)
                : null,
              number: policyNumber,
              subrogation_waived: !!waiverOfSubrogation
            },
            insurance_company: insuranceCompany,
            ...fuzzyAutoProperties
          };

          const fuzzyAutoEnhancedCorePolicyData = getEnhancedPolicyData(
            customAutoRequirement,
            camelcaseKeys(fuzzyAutoPolicyDetails?.insurance_company, { deep: true }),
            fuzzyAutoPolicyDetails
          );

          dispatch(setPolicyData(customAutoRequirement.id, fuzzyAutoEnhancedCorePolicyData));
        }
      }
    }
  }
};

export const setUmbExcPolicyData = (
  umbrellaPolicies,
  insuranceCompaniesByLetter,
  umbType,
  umbRequired,
  dispatch,
  setPolicyData,
  policyRequirements
) => {
  if (umbrellaPolicies?.length) {
    const firstUmbrellaPolicy = umbrellaPolicies[0];

    const {
      base_details: baseDetails,
      policy_details: policyDetails
    } = firstUmbrellaPolicy || {};

    const {
      insurer_letter: insurerLetter,
      policy_number: policyNumber,
      effective_date: effectiveDate,
      expiration_date: expirationDate,
      additional_insured: additionalInsured,
      waiver_of_subrogation: waiverOfSubrogation
    } = getBaseDetails(baseDetails);

    const {
      umbrella_liability_checkbox: umbrellaLiability,
      excess_liability_checkbox: excessLiability,
      occurrence_checkbox: occurrence,
      claims_made_checkbox: claimsMade,
      limits
    } = policyDetails || {};

    const {
      each_occurrence: eachOccurrenceLimit,
      aggregate: aggregateLimit,
      retention: retentionLimit,
      deduction: deductionLimit
    } = limits || {};

    const umbrellaPropertyMap = {
      umbrella_excess_excess_liability: 'excess',
      umbrella_excess_umbrella_liability: 'umbrella',
      umbrella_excess_claims_made: 'umb_exc_claims_made',
      umbrella_excess_deductible: 'umb_exc_deductible',
      umbrella_excess_occurrence: 'umb_exc_occurrence',
      umbrella_excess_retention: 'umb_exc_retention',
      umbrella_excess_limits_agg: 'limits_agg',
      umbrella_excess_limits_each_occur: 'limits_each_occur',
      umbrella_excess_deductible_limit: 'umb_exc_limit_deductible',
      umbrella_excess_retention_limit: 'umb_exc_limit_retention'

    };

    const insuranceCompany = insuranceCompaniesByLetter[insurerLetter] ?? null;
    const umbProperties = umbType?.properties;

    if (umbRequired) {
      const umbDefinedProperties = {
        [`properties_${umbProperties.find(prop => prop.name === umbrellaPropertyMap.umbrella_excess_umbrella_liability).id}`]: getCheckboxValueFromObject(umbrellaLiability),
        [`properties_${umbProperties.find(prop => prop.name === umbrellaPropertyMap.umbrella_excess_excess_liability).id}`]: getCheckboxValueFromObject(excessLiability),
        [`properties_${umbProperties.find(prop => prop.name === umbrellaPropertyMap.umbrella_excess_claims_made).id}`]: getCheckboxValueFromObject(claimsMade),
        [`properties_${umbProperties.find(prop => prop.name === umbrellaPropertyMap.umbrella_excess_occurrence).id}`]: getCheckboxValueFromObject(occurrence),
        [`properties_${umbProperties.find(prop => prop.name === umbrellaPropertyMap.umbrella_excess_deductible).id}`]: getCheckboxValueFromObject(deductionLimit),
        [`properties_${umbProperties.find(prop => prop.name === umbrellaPropertyMap.umbrella_excess_retention).id}`]: getCheckboxValueFromObject(retentionLimit),
        [`properties_${umbProperties.find(prop => prop.name === umbrellaPropertyMap.umbrella_excess_limits_agg).id}`]: getLimitValueFromObject(aggregateLimit),
        [`properties_${umbProperties.find(prop => prop.name === umbrellaPropertyMap.umbrella_excess_limits_each_occur).id}`]: getLimitValueFromObject(eachOccurrenceLimit),
        [`properties_${umbProperties.find(prop => prop.name === umbrellaPropertyMap.umbrella_excess_deductible_limit).id}`]: getLimitValueFromObject(deductionLimit),
        [`properties_${umbProperties.find(prop => prop.name === umbrellaPropertyMap.umbrella_excess_retention_limit).id}`]: getLimitValueFromObject(retentionLimit)
      };

      const umbPolicyDetails = {
        details: {
          additional_insured: !!additionalInsured,
          effective_at: isValidDateString(effectiveDate)
            ? new Date(effectiveDate)
            : null,
          expires_at: isValidDateString(expirationDate)
            ? new Date(expirationDate)
            : null,
          number: policyNumber,
          subrogation_waived: !!waiverOfSubrogation
        },
        insurance_company: insuranceCompany,
        ...umbDefinedProperties
      };

      const enhancedUmbPolicyData = getEnhancedPolicyData(
        umbType,
        camelcaseKeys(umbPolicyDetails?.insurance_company, { deep: true }),
        umbPolicyDetails
      );

      dispatch(setPolicyData(umbType.id, enhancedUmbPolicyData));
    } else {
      const policyTypeSearcher = new FuzzySearch(policyRequirements, ['displayName', 'name'], { sort: true });
      const fuzzyMatchUmbTerms = ['umbrella/excess', 'umbrella excess', 'umbrella', 'excess'];

      // eslint-disable-next-line no-plusplus
      for (let i = 0; i < fuzzyMatchUmbTerms.length; i++) {
        const fuzzyMatchName = fuzzyMatchUmbTerms[i];
        const fuzzyMatch = policyTypeSearcher.search(fuzzyMatchName);

        if (fuzzyMatch.length) {
          const customUmbRequirement = fuzzyMatch[0];

          const propertySearcher = new FuzzySearch(umbProperties, ['displayName', 'name'], { sort: true });
          const fuzzyUmbProperties = {};

          const umbrellaProperty = fuzzyMatchSearch(propertySearcher, ['Umbrella Liability', 'Umbrella Liab', 'Umbrella'], DataTypes.Boolean);
          const excessProperty = fuzzyMatchSearch(propertySearcher, ['Excess Liability', 'Excess Liab', 'Excess'], DataTypes.Boolean);
          const occurProperty = fuzzyMatchSearch(propertySearcher, ['Occur'], DataTypes.Boolean);
          const claimsMadeProperty = fuzzyMatchSearch(propertySearcher, ['Claims-Made', 'Claims Made'], DataTypes.Monetary);
          const eachOccurProperty = fuzzyMatchSearch(propertySearcher, ['Each Occurrence', 'Each Occurence', 'Each Ocurrence', 'Occurrence', 'Ea Occur'], DataTypes.Monetary);
          const aggProperty = fuzzyMatchSearch(propertySearcher, ['Aggregate', 'Agregate'], DataTypes.Monetary);

          if (umbrellaProperty) {
            fuzzyUmbProperties[`properties_${umbrellaProperty.id}`] = getCheckboxValueFromObject(umbrellaLiability);
          }

          if (excessProperty) {
            fuzzyUmbProperties[`properties_${excessProperty.id}`] = getCheckboxValueFromObject(excessLiability);
          }

          if (occurProperty) {
            fuzzyUmbProperties[`properties_${occurProperty.id}`] = getCheckboxValueFromObject(occurrence);
          }

          if (claimsMadeProperty) {
            fuzzyUmbProperties[`properties_${claimsMadeProperty.id}`] = getCheckboxValueFromObject(claimsMade);
          }

          if (eachOccurProperty) {
            fuzzyUmbProperties[`properties_${eachOccurProperty.id}`] = getLimitValueFromObject(eachOccurrenceLimit);
          }

          if (aggProperty) {
            fuzzyUmbProperties[`properties_${aggProperty.id}`] = getLimitValueFromObject(aggregateLimit);
          }

          const fuzzyUmbPolicyDetails = {
            details: {
              additional_insured: !!additionalInsured,
              effective_at: isValidDateString(effectiveDate)
                ? new Date(effectiveDate)
                : null,
              expires_at: isValidDateString(expirationDate)
                ? new Date(expirationDate)
                : null,
              number: policyNumber,
              subrogation_waived: !!waiverOfSubrogation
            },
            insurance_company: insuranceCompany,
            ...fuzzyUmbProperties
          };

          const fuzzyGlEnhancedCorePolicyData = getEnhancedPolicyData(
            customUmbRequirement,
            camelcaseKeys(fuzzyUmbPolicyDetails?.insurance_company, { deep: true }),
            fuzzyUmbPolicyDetails
          );

          dispatch(setPolicyData(customUmbRequirement.id, fuzzyGlEnhancedCorePolicyData));
        }
      }
    }
  }
};

export const setWorkersCompPolicyData = (
  workersPolicies,
  insuranceCompaniesByLetter,
  workersType,
  workersRequired,
  dispatch,
  setPolicyData,
  policyRequirements
) => {
  if (workersPolicies?.length) {
    const firstWorkersPolicy = workersPolicies[0];

    const {
      base_details: baseDetails,
      policy_details: policyDetails
    } = firstWorkersPolicy || {};

    const {
      insurer_letter: insurerLetter,
      policy_number: policyNumber,
      effective_date: effectiveDate,
      expiration_date: expirationDate,
      additional_insured: additionalInsured,
      waiver_of_subrogation: waiverOfSubrogation
    } = getBaseDetails(baseDetails);

    const {
      any_excluded_checkbox: anyExcluded,
      per_statute_checkbox: perStatute,
      other_checkbox: other,
      limits
    } = policyDetails || {};

    const {
      each_accident: eachAccidentLimit,
      disease_policy_limit: diseasePolicyLimit,
      disease_each_employee: diseaseEachEmployeeLimit
    } = limits || {};

    const wcPropertyMap = {
      workers_comp_checked: 'any_excluded',
      workers_comp_limits_statutory_checked: 'per_statute',
      workers_comp_limits_other_checked: 'other',
      workers_comp_limits_each_accident: 'limits_each_accident',
      workers_comp_limits_disease_each_emp: 'limits_disease_each_employee',
      workers_comp_limits_disease_policy: 'limits_disease_policy'
    };

    const insuranceCompany = insuranceCompaniesByLetter[insurerLetter] ?? null;
    const workersProperties = workersType?.properties;

    if (workersRequired) {
      const workersDefinedProperties = {
        [`properties_${workersProperties.find(prop => prop.name === wcPropertyMap.workers_comp_checked).id}`]: getCheckboxValueFromObject(anyExcluded),
        [`properties_${workersProperties.find(prop => prop.name === wcPropertyMap.workers_comp_limits_statutory_checked).id}`]: getCheckboxValueFromObject(perStatute),
        [`properties_${workersProperties.find(prop => prop.name === wcPropertyMap.workers_comp_limits_other_checked).id}`]: getCheckboxValueFromObject(other),
        [`properties_${workersProperties.find(prop => prop.name === wcPropertyMap.workers_comp_limits_each_accident).id}`]: getLimitValueFromObject(eachAccidentLimit),
        [`properties_${workersProperties.find(prop => prop.name === wcPropertyMap.workers_comp_limits_disease_each_emp).id}`]: getLimitValueFromObject(diseaseEachEmployeeLimit),
        [`properties_${workersProperties.find(prop => prop.name === wcPropertyMap.workers_comp_limits_disease_policy).id}`]: getLimitValueFromObject(diseasePolicyLimit)
      };

      const wcPolicyDetails = {
        details: {
          additional_insured: !!additionalInsured,
          effective_at: isValidDateString(effectiveDate)
            ? new Date(effectiveDate)
            : null,
          expires_at: isValidDateString(expirationDate)
            ? new Date(expirationDate)
            : null,
          number: policyNumber,
          subrogation_waived: !!waiverOfSubrogation
        },
        insurance_company: insuranceCompany,
        ...workersDefinedProperties
      };

      const enhancedWcPolicyData = getEnhancedPolicyData(
        workersType,
        camelcaseKeys(wcPolicyDetails?.insurance_company, { deep: true }),
        wcPolicyDetails
      );

      dispatch(setPolicyData(workersType.id, enhancedWcPolicyData));
    } else {
      const policyTypeSearcher = new FuzzySearch(policyRequirements, ['displayName', 'name'], { sort: true });
      const fuzzyMatchWcTerms = ['workers compensation', 'workers comp'];

      // eslint-disable-next-line no-plusplus
      for (let i = 0; i < fuzzyMatchWcTerms.length; i++) {
        const fuzzyMatchName = fuzzyMatchWcTerms[i];
        const fuzzyMatch = policyTypeSearcher.search(fuzzyMatchName);

        if (fuzzyMatch.length) {
          const customWcRequirement = fuzzyMatch[0];
          const propertySearcher = new FuzzySearch(workersProperties, ['displayName', 'name'], { sort: true });
          const fuzzyWcProperties = {};

          const anyExcludedProperty = fuzzyMatchSearch(propertySearcher, ['Any Excluded', 'Excluded'], DataTypes.Boolean);
          const perStatuteProperty = fuzzyMatchSearch(propertySearcher, ['Per Statute', 'Statute'], DataTypes.Boolean);
          const otherProperty = fuzzyMatchSearch(propertySearcher, ['Other'], DataTypes.Boolean);
          const eachAccidentProperty = fuzzyMatchSearch(propertySearcher, ['E.L. Each Accident', 'E.L. Ea Accident', 'EL Each', 'Each Accident'], DataTypes.Monetary);
          const diseaseEachEmployeeProperty = fuzzyMatchSearch(
            propertySearcher,
            ['E.L. Disease Each Employee', 'Disease Each Employee', 'Ea Employee', 'Employee'],
            DataTypes.Monetary
          );
          const diseasePolicyProperty = fuzzyMatchSearch(propertySearcher, ['E.L. Disease Policy Limit', 'Disease Policy Limit', 'Policy Limit', 'Policy'], DataTypes.Monetary);

          if (anyExcludedProperty) {
            fuzzyWcProperties[`properties_${anyExcludedProperty.id}`] = getCheckboxValueFromObject(anyExcluded);
          }

          if (perStatuteProperty) {
            fuzzyWcProperties[`properties_${perStatuteProperty.id}`] = getCheckboxValueFromObject(perStatute);
          }

          if (otherProperty) {
            fuzzyWcProperties[`properties_${otherProperty.id}`] = getCheckboxValueFromObject(other);
          }

          if (eachAccidentProperty) {
            fuzzyWcProperties[`properties_${eachAccidentProperty.id}`] = getLimitValueFromObject(eachAccidentLimit);
          }

          if (diseaseEachEmployeeProperty) {
            fuzzyWcProperties[`properties_${diseaseEachEmployeeProperty.id}`] = getLimitValueFromObject(diseaseEachEmployeeLimit);
          }

          if (diseasePolicyProperty) {
            fuzzyWcProperties[`properties_${diseasePolicyProperty.id}`] = getLimitValueFromObject(diseasePolicyLimit);
          }

          const fuzzyWcPolicyDetails = {
            details: {
              additional_insured: !!additionalInsured,
              effective_at: isValidDateString(effectiveDate)
                ? new Date(effectiveDate)
                : null,
              expires_at: isValidDateString(expirationDate)
                ? new Date(expirationDate)
                : null,
              number: policyNumber,
              subrogation_waived: !!waiverOfSubrogation
            },
            insurance_company: insuranceCompany,
            ...fuzzyWcProperties
          };

          const fuzzyWcEnhancedCorePolicyData = getEnhancedPolicyData(
            customWcRequirement,
            camelcaseKeys(fuzzyWcPolicyDetails?.insurance_company, { deep: true }),
            fuzzyWcPolicyDetails
          );

          dispatch(setPolicyData(customWcRequirement.id, fuzzyWcEnhancedCorePolicyData));
        }
      }
    }
  }
};

export const setOtherPolicyData = (
  otherPolicies,
  insuranceCompaniesByLetter,
  companyPolicyTypes,
  dispatch,
  setPolicyData,
  policyRequirements,
  existingPolicyData
) => {
  const customPolicyTypesAlreadyAdded = {};

  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < otherPolicies.length; i++) {
    const otherPolicy = otherPolicies[i];
    const {
      base_details: baseDetails,
      policy_details: policyDetails
    } = otherPolicy || {};

    const {
      insurer_letter: insurerLetter,
      policy_number: policyNumber,
      effective_date: effectiveDate,
      expiration_date: expirationDate,
      additional_insured: additionalInsured,
      waiver_of_subrogation: waiverOfSubrogation
    } = getBaseDetails(baseDetails);

    // pull policyType name separately
    const policyType = baseDetails?.policy_type?.value;

    if (!policyType) {
      // eslint-disable-next-line no-continue
      continue;
    }

    const { limits } = policyDetails || {};

    const insuranceCompany = insuranceCompaniesByLetter[insurerLetter] ?? null;

    const corePolicyTypeNames = [
      PolicyTypes.General,
      PolicyTypes.Auto,
      PolicyTypes.UmbrellaExcess,
      PolicyTypes.WorkersComp
    ];

    const customCompanyPolicyTypesNameMap = companyPolicyTypes.reduce((acc, cur) => {
      const policyTypeIsInRequirements = policyRequirements.find(pR => pR.id === cur.id);
      const alreadyAdded = customPolicyTypesAlreadyAdded[cur.id];

      if (!alreadyAdded && !corePolicyTypeNames.includes(cur.name) && policyTypeIsInRequirements) {
        acc[cur.displayName] = cur;
      }

      return acc;
    }, {});

    console.log('Making the map', { customCompanyPolicyTypesNameMap, policyRequirements, customPolicyTypesAlreadyAdded });

    // see if this policy has a matching policy type for the company
    const customPolicyTypeFuzzyFindName = customFuzzyFind(policyType, Object.keys(customCompanyPolicyTypesNameMap));

    console.log('Finding a fuzzy match', { customCompanyPolicyTypesNameMap, policyType, customPolicyTypeFuzzyFindName });

    // if it does, see if it exists in the policy requirements
    if (customPolicyTypeFuzzyFindName?.match) {
      const customPolicyType = customCompanyPolicyTypesNameMap[customPolicyTypeFuzzyFindName?.match];
      const customPolicyTypeId = customPolicyType.id;

      // see if this id exists in the policy requirements
      // TODO: remove this and always add the policy regardless if it's required or not
      const customPolicyRequirementType = policyRequirements.filter(pr => !corePolicyTypeNames.includes(pr.name))
        .find(p => p.id === customPolicyTypeId);

      // if a policy requirement exists, add the other_policy to this policyRequirement
      if (customPolicyRequirementType?.id) {
        console.log('Fuzzy match found, finding properties', { customPolicyRequirementType });

        const policyRequirementProperties = customPolicyRequirementType.properties;

        const polReqPropNameMap = policyRequirementProperties.reduce((acc, cur) => {
          acc[cur.displayName] = cur;

          return acc;
        }, {});

        const otherProperties = {};

        // limits is a model-generated array here (unlike the fixed limit objects on the
        // core policy types) and may be absent entirely
        // eslint-disable-next-line no-plusplus
        for (let j = 0; j < (limits?.length ?? 0); j++) {
          const limit = limits[j];
          const { limit_name: limitName, limit_value: limitValue, confidence } = limit || {};

          const limitPropertyMatch = customFuzzyFind(limitName, Object.keys(polReqPropNameMap));

          if (limitPropertyMatch?.match) {
            const limitProperty = polReqPropNameMap[limitPropertyMatch?.match];

            console.log('Fuzzy match found for a property', { limitPropertyMatch, limitProperty, polReqPropNameMap });

            if (limitProperty?.id) {
              otherProperties[`properties_${limitProperty.id}`] = limitValue ? `$${limitValue}` : '';
            }
          }
        }

        const fuzzyOtherPolicyDetails = {
          details: {
            additional_insured: !!additionalInsured,
            effective_at: isValidDateString(effectiveDate)
              ? new Date(effectiveDate)
              : null,
            expires_at: isValidDateString(expirationDate)
              ? new Date(expirationDate)
              : null,
            number: policyNumber,
            subrogation_waived: !!waiverOfSubrogation
          },
          insurance_company: insuranceCompany,
          ...otherProperties
        };

        const fuzzyOtherEnhancedPolicyData = getEnhancedPolicyData(
          customPolicyRequirementType,
          camelcaseKeys(fuzzyOtherPolicyDetails?.insurance_company, { deep: true }),
          fuzzyOtherPolicyDetails
        );

        dispatch(setPolicyData(customPolicyRequirementType.id, fuzzyOtherEnhancedPolicyData));

        customPolicyTypesAlreadyAdded[customPolicyRequirementType.id] = true;
      }
    }
  }
};

const fuzzyMatchSearch = (fuzzyMatchSearcher, searchWords = [], dataType = null) => {
  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < searchWords.length; i++) {
    const searchWord = searchWords[i].toLowerCase();
    const propertySearch = fuzzyMatchSearcher.search(searchWord);

    if (propertySearch.length) {
      if (dataType) {
        const property = propertySearch.find(p => p.dataType === dataType);

        if (property) {
          return property;
        }
      } else {
        return propertySearch[0];
      }
    }
  }

  return null;
};

// the scan response is model-generated: any field can be absent or null, so every
// getter below treats a missing object as "not found" rather than destructuring it
const getCheckboxValueFromObject = checkBoxObject => {
  const { value, checkbox_state: state, confidence } = checkBoxObject || {};

  if ([CheckBoxStates.Unclear, CheckBoxStates.NotPresent].includes(state)) {
    return false;
  }

  if (confidence < 0.6) {
    return false;
  }

  if (value === false && state === CheckBoxStates.Unchecked) {
    return false;
  }

  return value === true && state === CheckBoxStates.Checked;
};

const getLimitValueFromObject = limitObject => {
  const { value, confidence } = limitObject || {};

  if (confidence < 0.6) {
    return null;
  }

  return value ? `$${value}` : '';
};

const getBaseDetailValueFromObject = baseObject => {
  const { confidence, value } = baseObject || {};

  if (confidence < 0.6) {
    return null;
  }

  return value ?? null;
};

const getBaseDetails = baseDetailsObject => {
  const {
    insurer_letter: insurerLetter,
    policy_number: policyNumber,
    effective_date: effectiveDate,
    expiration_date: expirationDate,
    additional_insured: additionalInsured,
    waiver_of_subrogation: waiverOfSubrogation
  } = baseDetailsObject || {};

  return {
    insurer_letter: getBaseDetailValueFromObject(insurerLetter),
    policy_number: getBaseDetailValueFromObject(policyNumber),
    effective_date: getBaseDetailValueFromObject(effectiveDate),
    expiration_date: getBaseDetailValueFromObject(expirationDate),
    additional_insured: getBaseDetailValueFromObject(additionalInsured),
    waiver_of_subrogation: getBaseDetailValueFromObject(waiverOfSubrogation)
  };
};

const CheckBoxStates = {
  Checked: 'checked',
  Unchecked: 'unchecked',
  Unclear: 'unclear',
  NotPresent: 'not_present'
};
